import React, { useMemo } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { TableView } from '~components/table-view';

import serviceTypeColumns from '~support-at-home/forms/sah-associations/cutoverServiceTypes';

import { CutoverContext, useGetCutoverServiceTypesQuery } from '~support-at-home/actions/sahServiceCutover';

import { usePublishedValue, useSeededSelection } from './hooks/useSelectionSync';
import StepPanel from './StepPanel';

interface ComponentProps {
  value: CutoverContext & { service_type_ids?: number[] };
  onChange?: (name: string, value: any) => any;
}

/**
 * Step 2 - the local service types associated with the services being cut over.
 *
 * The provider chooses which of them to associate with the replacement, and that choice narrows
 * every step after this one: the price plans looked at are the ones carrying these service types,
 * and the budget lines looked at are the ones booking them.
 *
 * A service type already associated with the replacement is pre-selected and left selectable but
 * inert - confirming does not create it a second time. Showing it rather than hiding it is what
 * tells a provider picking up an interrupted run what the earlier run already did.
 */
/** The distinct service types behind a set of selected rows, since a type can appear on several. */
const toServiceTypeIds = (rows: Array<{ id: string; service_type_id: number }>, selected: string[]) => {
  const chosen = new Set(selected);
  return [...new Set(rows.filter(({ id }) => chosen.has(id)).map(({ service_type_id }) => service_type_id))];
};

const ServiceTypeStep: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { cutover_date, pairs = [] } = value;

  const { data, isFetching, error } = useGetCutoverServiceTypesQuery(
    { cutover_date, pairs },
    { skip: !cutover_date || pairs.length === 0, refetchOnMountOrArgChange: true },
  );

  const rows = useMemo(() => (data?.rows ?? []).map((row) => ({ ...row, id: String(row.service_type_id) })), [data]);

  // Everything found is selected by default: the provider is here to carry the whole service across,
  // and de-selecting the odd one is the exception.
  const [selectedIds, handleSelection] = useSeededSelection(
    rows,
    data !== undefined,
    (allRows) => allRows.map(({ id }) => id),
    (ids) => onChange?.('service_type_ids', toServiceTypeIds(rows, ids)),
  );

  // What confirming will actually create: selected AND not already associated. Counting the whole
  // selection would promise work on rows the assistant is going to skip, and counting every
  // outstanding row would promise work on rows the provider has just deselected. Published to the
  // form as well, so the confirmation dialog quotes the same number this panel does.
  const pending = rows.filter(({ id, already_associated }) => selectedIds.includes(id) && !already_associated).length;

  usePublishedValue(pending, (next) => onChange?.('service_types_pending', next));

  return (
    <StepPanel
      id="cutover-service-types"
      header={`${rows.length} associated service type${rows.length === 1 ? '' : 's'}`}
      isFetching={isFetching}
      error={error}
      errorMessage={error ? getErrorMessage(error) : undefined}
      isComplete={Boolean(data?.is_complete)}
      hasRows={rows.length > 0}
      emptyMessage="No local service types are associated with any of the services being cut over, so there is nothing to carry across."
      completeMessage="Every one of these service types is already associated with the replacement. This step is already done - continue to the next one."
      outcome={[
        `Associate ${pending} service type${pending === 1 ? '' : 's'} with the replacement service. Anything already associated, or not selected, is left alone.`,
        'The service types selected here decide which price plans and which budget lines the next two steps look at.',
        'Stopping after this step leaves the associations in place. Prices and budgets are untouched until their own steps are confirmed.',
      ]}
      warning="Confirming to the next step will associate these service types, please ensure you have verified your selection."
    >
      <TableView
        id="cutover-service-types-table"
        data={rows}
        columns={serviceTypeColumns}
        readonly
        striped
        canSelect
        selectedIds={selectedIds}
        onRowSelectionChange={handleSelection}
        showAddRow={false}
      />
    </StepPanel>
  );
};

export default ServiceTypeStep;
